import { Button, Dialog, IconButton } from '@mui/material';
import FormFooter, { FormFooterProps } from 'components/shared/FormFooter';
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import ApplicationContent, { ApplicationContentSectionView } from 'store/types/ApplicationContent';
import { getFormattedSections, getReviewSectionId, getSectionId } from 'util/Application';
import { useWindowSize } from 'util/Window';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import classNames from 'classnames';

import styles from './ApplicationPageLayout.module.scss';

interface ApplicationPageLayoutProps {
  data?: ApplicationContent;
  footerProps?: PropsWithChildren<FormFooterProps>;
  isReviewForm?: boolean;
  isPrintActive?: boolean;
}

const ApplicationPageLayout: React.FunctionComponent<ApplicationPageLayoutProps> = ({
  data,
  footerProps,
  children,
  isReviewForm,
  isPrintActive,
}) => {
  const [sections, setSections] = useState<ApplicationContentSectionView[]>([]);
  const [collapsed, setCollapsed] = useState<boolean | null>(null);
  const [activeSectionIndex, setActiveSectionIndex] = useState<number>(0);
  const { isMobile } = useWindowSize();

  useEffect(() => {
    if (data) {
      setSections(getFormattedSections(data.sections));
    }
  }, [data]);

  useEffect(() => {
    if (isMobile && collapsed === null) {
      setCollapsed(true);
    }
  }, [collapsed, isMobile]);

  const handleMenuButtonClick = () => {
    setCollapsed(!collapsed);
  };

  useEffect(() => {
    if (!sections.length) return;

    const topMargin = isReviewForm ? '-90px' : '0px';

    let topSentinelVisible = false;
    let bottomSentinelVisible = false;

    const intersectingSections = new Map<number, Element>();

    const updateActiveSection = () => {
      if (topSentinelVisible) {
        setActiveSectionIndex(0);
        return;
      }

      if (bottomSentinelVisible) {
        setActiveSectionIndex(sections.length - 1);
        return;
      }

      const visibleIndexes = Array.from(intersectingSections.keys()).sort((a, b) => a - b);

      if (!visibleIndexes.length) return;

      const nextIndex = visibleIndexes[0];

      setActiveSectionIndex(nextIndex);
    };

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number(entry.target.getAttribute('data-section-index'));
          if (Number.isNaN(index)) return;

          if (entry.isIntersecting) {
            intersectingSections.set(index, entry.target);
          } else {
            intersectingSections.delete(index);
          }
        });

        updateActiveSection();
      },
      {
        root: null,
        rootMargin: `${topMargin} 0px -90% 0px`,
        threshold: 0,
      }
    );

    const topObserver = new IntersectionObserver(
      ([entry]) => {
        topSentinelVisible = entry.isIntersecting;
        updateActiveSection();
      },
      {
        root: null,
        threshold: 0,
      }
    );

    const bottomObserver = new IntersectionObserver(
      ([entry]) => {
        bottomSentinelVisible = entry.isIntersecting;
        updateActiveSection();
      },
      {
        root: null,
        threshold: 0,
      }
    );

    sections.forEach((_, index) => {
      const element = document.getElementById(getSectionId(index));
      if (!element) return;

      element.setAttribute('data-section-index', String(index));
      sectionObserver.observe(element);
    });

    const topSentinel = document.getElementById('form-top-sentinel');
    if (topSentinel) {
      topObserver.observe(topSentinel);
    }

    const bottomSentinel = document.getElementById('form-bottom-sentinel');
    if (bottomSentinel) {
      bottomObserver.observe(bottomSentinel);
    }

    updateActiveSection();

    return () => {
      sectionObserver.disconnect();
      topObserver.disconnect();
      bottomObserver.disconnect();
    };
  }, [sections, children, isReviewForm]);

  const handleLinkClick = useCallback(
    (index: number) => () => {
      const sectionElement: HTMLElement | null = document.getElementById(getSectionId(index));
      const reviewSectionElement: HTMLElement | null = document.getElementById(getReviewSectionId(index));

      if (sectionElement) {
        sectionElement.scrollIntoView();
      }
      if (reviewSectionElement) {
        reviewSectionElement.scrollIntoView();
      }
    },
    []
  );

  const headerOffset = isReviewForm ? '90px' : '0px';

  return (
    <>
      <Dialog
        open={true}
        fullScreen={true}
        className={classNames({ [styles.collapsed]: collapsed })}
        style={{ '--header-offset': headerOffset } as React.CSSProperties}
      >
        {!isPrintActive && (
          <aside className={styles.fullPageSidebar}>
            <ul>
              {sections.map((section, index) => {
                const isActive = activeSectionIndex === index;

                return (
                  <li
                    key={`${section.title}${index}-icon`}
                    className={classNames(styles.link, { [styles.activeLink]: isActive })}
                    onClick={handleLinkClick(index)}
                  >
                    <IconButton
                      classes={{ root: styles.linkIcon }}
                      color={isActive ? 'secondary' : 'primary'}
                      sx={isActive ? { backgroundColor: 'action.selected' } : {}}
                    >
                      {++index}
                    </IconButton>
                    <span className={styles.linkLabel}>{section.title}</span>
                  </li>
                );
              })}
            </ul>
            <Button variant={'text'} onClick={handleMenuButtonClick} className={styles.menuButton}>
              {collapsed ? <KeyboardDoubleArrowRightIcon /> : <KeyboardDoubleArrowLeftIcon />}
            </Button>
          </aside>
        )}
        <div className={classNames({ [styles.fullPageContent]: !isPrintActive })}>{children}</div>
        {!isPrintActive && <FormFooter {...footerProps} className={styles.fullPageFormFooter} />}
      </Dialog>
    </>
  );
};
export default ApplicationPageLayout;
